import React from "react";
import {
  AlertTriangle,
  ArrowDown,
  BadgeCheck,
  BellRing,
  CheckCircle2,
  Clock3,
  DatabaseZap,
  FileCheck2,
  Globe2,
  LocateFixed,
  LockKeyhole,
  Mail,
  Megaphone,
  MessageSquareText,
  PenLine,
  RefreshCcw,
  Route,
  ServerCog,
  ShieldCheck,
  UserRoundCheck,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { JourneySource } from "./JourneySourceSelector";

interface StepProps {
  number: number;
  eyebrow: string;
  title: string;
  description: string;
  icon: LucideIcon;
  tone: string;
  children?: React.ReactNode;
}

const SOURCE_CONTENT: Record<JourneySource, {
  eyebrow: string;
  title: string;
  description: string;
  icon: LucideIcon;
  badges: string[];
}> = {
  meta: {
    eyebrow: "Meta Lead Ads",
    title: "Realtime παραλαβή από το Lineweb Lead Hub",
    description: "Το Hub παραδίδει identifier-only payload με HMAC. Ο client ανακτά τα στοιχεία από τη Meta μόνο όταν Page και form mapping είναι ενεργά.",
    icon: Megaphone,
    badges: ["Page + form mapping", "HMAC handoff", "fb_lead_id"],
  },
  web: {
    eyebrow: "Φόρμα ιστοσελίδας",
    title: "Server-side validation της δημόσιας υποβολής",
    description: "Το δημόσιο endpoint ελέγχει τα επιτρεπτά πεδία και δημιουργεί το lead μέσα στην ίδια ασφαλή ροή ανάθεσης.",
    icon: Globe2,
    badges: ["Public lead contract", "Validation", "Κοινό routing"],
  },
  manual: {
    eyebrow: "Admin καταχώρηση",
    title: "Χειροκίνητο lead με ελεγχόμενη ειδοποίηση",
    description: "Η ομάδα μπορεί να επιλέξει συνεργάτη ή να χρησιμοποιήσει το ίδιο matcher, με ρητή επιλογή για δημιουργία notification jobs.",
    icon: PenLine,
    badges: ["Admin session", "Manual ή auto match", "Notify control"],
  },
};

function FlowConnector({ label }: { label?: string }) {
  return (
    <div className="flex items-center gap-3 py-2 pl-5 sm:pl-7" aria-hidden="true">
      <span className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 shadow-sm">
        <ArrowDown className="h-4 w-4" />
      </span>
      {label && <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">{label}</span>}
    </div>
  );
}

function Step({ number, eyebrow, title, description, icon: Icon, tone, children }: StepProps) {
  return (
    <article className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className={`absolute inset-y-0 left-0 w-1 ${tone}`} />
      <div className="p-4 pl-5 sm:p-5 sm:pl-6">
        <div className="flex items-start gap-3.5">
          <span className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-700 ring-1 ring-slate-200">
            <Icon className="h-5 w-5" />
            <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-slate-950 px-1 text-[10px] font-black text-white ring-2 ring-white">
              {number}
            </span>
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-400">{eyebrow}</p>
            <h2 className="mt-1 text-base font-bold leading-6 tracking-tight text-slate-950 sm:text-lg">{title}</h2>
            <p className="mt-1.5 text-sm leading-6 text-slate-500">{description}</p>
          </div>
        </div>
        {children && <div className="mt-4 border-t border-slate-100 pt-4 sm:ml-[3.65rem]">{children}</div>}
      </div>
    </article>
  );
}

function MiniBadge({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex min-h-7 items-center rounded-full bg-slate-100 px-2.5 text-[11px] font-bold text-slate-600 ring-1 ring-inset ring-slate-200">
      {children}
    </span>
  );
}

export function JourneyFlow({ source }: { source: JourneySource }) {
  const sourceContent = SOURCE_CONTENT[source];

  return (
    <section aria-label="Διάγραμμα ροής lead" className="min-w-0">
      <Step
        number={1}
        eyebrow={sourceContent.eyebrow}
        title={sourceContent.title}
        description={sourceContent.description}
        icon={sourceContent.icon}
        tone="bg-sky-500"
      >
        <div className="flex flex-wrap gap-2">
          {sourceContent.badges.map((badge) => <MiniBadge key={badge}>{badge}</MiniBadge>)}
        </div>
      </Step>

      <FlowConnector label="Κοινή ασφαλής ροή" />

      <Step
        number={2}
        eyebrow="Normalize · locate · deduplicate"
        title="Έλεγχος στοιχείων και γεωγραφικός εντοπισμός"
        description="Ο server κανονικοποιεί τα πεδία και χρησιμοποιεί ΤΚ, πόλη και διεύθυνση για geocoding. Αν η θέση δεν επαληθευτεί, το lead μένει unassigned· στα Meta leads, το fb_lead_id εγγυάται idempotency."
        icon={LocateFixed}
        tone="bg-orange-500"
      >
        <div className="grid gap-2 sm:grid-cols-3">
          {[
            { icon: FileCheck2, label: "Έγκυρα πεδία" },
            { icon: LocateFixed, label: "Συντεταγμένες" },
            { icon: BadgeCheck, label: "Χωρίς duplicate" },
          ].map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.label} className="flex min-h-11 items-center gap-2 rounded-xl bg-slate-50 px-3 text-xs font-bold text-slate-700 ring-1 ring-inset ring-slate-200">
                <Icon className="h-4 w-4 text-slate-400" /> {item.label}
              </div>
            );
          })}
        </div>
      </Step>

      <FlowConnector label="Service + coverage rules" />

      <Step
        number={3}
        eyebrow="Partner matching"
        title="Αναζήτηση κατάλληλου συνεργάτη"
        description="Συμμετέχουν μόνο ενεργοί συνεργάτες με τη σωστή υπηρεσία και πραγματική κάλυψη της θέσης. Αν υπάρχουν πολλοί, η επιλογή γίνεται με συνολικές αναθέσεις, απόσταση και σταθερό tie-break."
        icon={Route}
        tone="bg-teal-500"
      />

      <FlowConnector label="Αποτέλεσμα matcher" />

      <div className="grid gap-3 sm:grid-cols-2">
        <article className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4 sm:p-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-sm">
            <CheckCircle2 className="h-5 w-5" />
          </div>
          <p className="mt-4 text-[11px] font-bold uppercase tracking-[0.1em] text-emerald-700">Βρέθηκε συνεργάτης</p>
          <h2 className="mt-1 text-base font-bold text-emerald-950">Ανάθεση μέσα στο ίδιο transaction</h2>
          <p className="mt-2 text-sm leading-6 text-emerald-950/70">Το lead συνδέεται με τον συνεργάτη και δημιουργούνται idempotent notification_jobs πριν ολοκληρωθεί η εγγραφή.</p>
        </article>

        <article className="rounded-2xl border border-amber-200 bg-amber-50/70 p-4 sm:p-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500 text-white shadow-sm">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <p className="mt-4 text-[11px] font-bold uppercase tracking-[0.1em] text-amber-700">Δεν βρέθηκε συνεργάτης</p>
          <h2 className="mt-1 text-base font-bold text-amber-950">Μένει unassigned, χωρίς αυθαίρετη ανάθεση</h2>
          <p className="mt-2 text-sm leading-6 text-amber-950/70">Ανοίγει alert στο Operations Inbox και η ομάδα κάνει manual assignment με βάση την πραγματική κάλυψη.</p>
        </article>
      </div>

      <FlowConnector label="Όταν υπάρχει ανάθεση" />

      <Step
        number={4}
        eyebrow="Durable notification queue"
        title="Προγραμματισμένη και ελεγχόμενη παράδοση"
        description="Ο worker ελέγχει την ουρά ανά λεπτό. Κάθε κανάλι έχει μοναδικό idempotency key, έως 5 retries με backoff και μόνιμο alert αν ο provider αποτύχει οριστικά."
        icon={BellRing}
        tone="bg-violet-500"
      >
        <div className="grid gap-2.5">
          <div className="flex items-start gap-3 rounded-xl border border-sky-100 bg-sky-50/70 p-3">
            <Mail className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />
            <div>
              <p className="text-xs font-black text-sky-950">Email συνεργάτη · άμεσα</p>
              <p className="mt-0.5 text-xs leading-5 text-sky-900/65">Αποστέλλεται όταν υπάρχει email στον ενεργό συνεργάτη.</p>
            </div>
          </div>
          <div className="flex items-start gap-3 rounded-xl border border-violet-100 bg-violet-50/70 p-3">
            <MessageSquareText className="mt-0.5 h-4 w-4 shrink-0 text-violet-600" />
            <div>
              <p className="text-xs font-black text-violet-950">SMS συνεργάτη · 08:00–20:00</p>
              <p className="mt-0.5 text-xs leading-5 text-violet-900/65">Στις quiet hours προγραμματίζεται για τις 08:00. Opt-out σημαίνει suppressed, όχι failure.</p>
            </div>
          </div>
          <div className="flex items-start gap-3 rounded-xl border border-emerald-100 bg-emerald-50/70 p-3">
            <Mail className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
            <div>
              <p className="text-xs font-black text-emerald-950">Email επιβεβαίωσης πελάτη · αν υπάρχει email</p>
              <p className="mt-0.5 text-xs leading-5 text-emerald-900/65">Δεν δημιουργείται customer SMS από τη σημερινή durable queue.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <MiniBadge><ServerCog className="mr-1.5 h-3.5 w-3.5" /> Worker ανά λεπτό</MiniBadge>
            <MiniBadge><RefreshCcw className="mr-1.5 h-3.5 w-3.5" /> Έως 5 retries</MiniBadge>
            <MiniBadge><Clock3 className="mr-1.5 h-3.5 w-3.5" /> Quiet hours</MiniBadge>
          </div>
        </div>
      </Step>

      <FlowConnector label="Exact-lead access" />

      <Step
        number={5}
        eyebrow="Secure partner action"
        title="Ο συνεργάτης ανοίγει το συγκεκριμένο lead χωρίς υποχρεωτικό login"
        description="Το email και το SMS περιέχουν 30ήμερο signed link, δεμένο με το exact lead και τον τρέχοντα συνεργάτη. Το πλήρες portal παραμένει password protected."
        icon={LockKeyhole}
        tone="bg-emerald-500"
      >
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="rounded-xl bg-slate-50 p-3 ring-1 ring-inset ring-slate-200">
            <div className="flex items-center gap-2 text-xs font-black text-slate-900"><UserRoundCheck className="h-4 w-4 text-emerald-600" /> Scoped πρόσβαση</div>
            <p className="mt-1.5 text-xs leading-5 text-slate-500">Βλέπει μόνο το lead που του έχει ανατεθεί και η προβολή καταγράφεται.</p>
          </div>
          <div className="rounded-xl bg-slate-50 p-3 ring-1 ring-inset ring-slate-200">
            <div className="flex items-center gap-2 text-xs font-black text-slate-900"><FileCheck2 className="h-4 w-4 text-sky-600" /> Πραγματικά στάδια</div>
            <p className="mt-1.5 text-xs leading-5 text-slate-500">Νέο, Σε εκκρεμότητα, Ήθελε πληροφορίες, Έκλεισε η δουλειά.</p>
          </div>
        </div>
      </Step>

      <FlowConnector label="Ορατό στην ομάδα" />

      <Step
        number={6}
        eyebrow="Operations & lifecycle"
        title="Παρακολούθηση, SLA και ασφαλές reassignment"
        description="Η ομάδα βλέπει unassigned leads, retries, failures, Meta exceptions και καθυστερημένες αλλαγές σταδίου. Σε νέα ανάθεση ακυρώνονται τα μη εκτελεσμένα jobs του προηγούμενου συνεργάτη και δημιουργούνται idempotently τα νέα."
        icon={ShieldCheck}
        tone="bg-slate-700"
      >
        <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs font-semibold leading-5 text-slate-600">
          <DatabaseZap className="h-4 w-4 shrink-0 text-slate-400" /> Τα Apps Script / Google Sheets δεν αποτελούν πλέον ενεργό production intake.
        </div>
      </Step>
    </section>
  );
}
